import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { B2bTraining } from '../../services/b2bAcademyApi';
import { B2bTegel } from './B2bTegel';

/** Netflix-rij (swiper) van de B2B-catalogus met fiscale status-tag naast de titel en "Alles bekijken". */
export const B2bRij: React.FC<{
  id: string;
  titel: string;
  subtitel?: string;
  emoji?: string;
  accent?: string;
  fiscaalLabel?: string;
  wkr?: boolean;
  trainingen: B2bTraining[];
  onOpen: (training: B2bTraining) => void;
  onBoek?: (training: B2bTraining) => void;
  /** "Alles bekijken": de hele rij als grid (filter ?rij=). */
  onAlles?: () => void;
  /** Losse badge naast de titel, bijv. "Aanbevolen". */
  badge?: string;
}> = ({ id, titel, subtitel, emoji, accent, fiscaalLabel, wkr, trainingen, onOpen, onBoek, onAlles, badge }) => {
  const { t } = useTranslation('pages');
  const scroller = useRef<HTMLDivElement>(null);
  const [rand, setRand] = useState({ links: false, rechts: false });

  const meet = () => {
    const el = scroller.current;
    if (!el) return;
    setRand({ links: el.scrollLeft > 4, rechts: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  };

  useEffect(() => {
    meet();
    window.addEventListener('resize', meet);
    return () => window.removeEventListener('resize', meet);
  }, [trainingen.length]);

  const schuif = (richting: 1 | -1) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: richting * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  return (
    <section aria-labelledby={`b2b-rij-${id}`} className="relative">
      <div className="mb-2.5 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 id={`b2b-rij-${id}`} className="flex flex-wrap items-center gap-2 text-lg font-bold text-gray-900">
            {accent && <span className="h-5 w-1 rounded-full" style={{ backgroundColor: accent }} aria-hidden="true" />}
            {emoji && <span aria-hidden="true">{emoji}</span>}
            <span className="truncate">{titel}</span>
            {badge && <span className="inline-flex items-center rounded-full border border-[#E5A425]/50 bg-[#FAEDCD] px-2 py-0.5 text-[11px] font-semibold text-[#8a5a00]">{badge}</span>}
            {fiscaalLabel && (
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                  wkr ? 'border-orange-400/50 bg-orange-400/10 text-orange-700' : 'border-emerald-400/40 bg-emerald-400/10 text-emerald-700'
                }`}
              >
                {wkr ? '⚠️' : '💸'} {fiscaalLabel}
              </span>
            )}
          </h2>
          {subtitel && <p className="mt-0.5 text-xs text-gray-500">{subtitel}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {onAlles && (
            <button type="button" onClick={onAlles} className="mr-1 whitespace-nowrap text-xs font-semibold text-sky-700 hover:text-sky-900">
              {t('b2bAcademy.rij.alles', { defaultValue: 'Alles bekijken' })}
            </button>
          )}
          <div className="hidden gap-1.5 sm:flex">
            <button type="button" onClick={() => schuif(-1)} disabled={!rand.links} aria-label={t('b2bAcademy.rij.vorige', { defaultValue: 'Vorige' })} className="rounded-full border border-gray-300 bg-white p-1.5 text-gray-900 transition hover:bg-gray-100 disabled:opacity-25">
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => schuif(1)} disabled={!rand.rechts} aria-label={t('b2bAcademy.rij.volgende', { defaultValue: 'Volgende' })} className="rounded-full border border-gray-300 bg-white p-1.5 text-gray-900 transition hover:bg-gray-100 disabled:opacity-25">
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      <div ref={scroller} onScroll={meet} className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-3 pt-1 [scrollbar-width:none] sm:gap-4 [&::-webkit-scrollbar]:hidden">
        {trainingen.map((tr) => (
          <B2bTegel key={tr.id} training={tr} onOpen={onOpen} onBoek={onBoek} className="w-[82vw] max-w-[20rem] sm:w-[18rem]" />
        ))}
      </div>
    </section>
  );
};

export default B2bRij;
